/**
 * Studio API utilities
 * Handles authentication headers for staging environments
 */

/**
 * Get headers for Studio API requests.
 * In staging, includes the internal token header for authentication.
 */
export function getStudioHeaders(): HeadersInit {
    const headers: HeadersInit = {
        'Content-Type': 'application/json',
    };

    // Add internal token header for staging authentication
    const internalToken = import.meta.env['VITE_HIVECORE_INTERNAL_TOKEN'];
    if (internalToken) {
        headers['X-HiveCore-Internal-Token'] = internalToken;
    }

    return headers;
}

/**
 * Get the Studio API base URL.
 * Uses VITE_HIVECORE_API_URL (standardized).
 *
 * If not set, returns empty string so callers can use same-origin relative paths.
 */
export function getStudioApiBase(): string {
    const raw = import.meta.env['VITE_HIVECORE_API_URL'] || '';

    // Normalize: strip trailing slashes to prevent `//api/...` requests
    return raw ? raw.toString().trim().replace(/\/+$/, '') : '';
}

/**
 * Make a fetch request to the Studio API with proper authentication headers.
 */
export async function studioFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const baseUrl = getStudioApiBase();
    const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;

    const headers = {
        ...getStudioHeaders(),
        ...(options.headers || {}),
    };

    return fetch(url, {
        ...options,
        headers,
    });
}
